/**
 * งานยามออฟไลน์ (docs/systems/OFFLINE_WORK.md): what a character's body did while the player was away. The player
 * picks a job (data/offlineWork.ts) or gets the one their best trade suits; on return `settleAway` pays the
 * silver for the hours worked (capped as the offline แต้มฝึกปรือ is, cultivation.ts `offlineGain`), and draws,
 * from a seed the save fixes, whether someone blew knockout powder at the body and sold it: then the work
 * stopped at that moment and the character wakes beside the buyer.
 *
 * Pure rules over `PlayerProgress`: no DOM, no clock (the time away is given), no `Math.random` (the draw is
 * seeded by the save). The client calls it once at boot, before the World is built (the World's
 * systems/offlineWork.ts sets the character down where it woke). A server would call it the same way when a
 * character logs back in.
 */
import { CULTIVATION, offlineGain, rankFor } from './cultivation';
import {
  buyersIn,
  INFAMY,
  isOfflineJobId,
  KIDNAP,
  OFFLINE_JOB_LIST,
  OFFLINE_JOBS,
  OFFLINE_WORK,
  SACK_BUYERS,
  sackPrice,
  sackVictimLoss,
  spotsFor,
  type OfflineJobId,
  type SackBuyer,
} from './data/offlineWork';
import { tradeLevel } from './professions';
import type { PlayerProgress } from './progress';
import { mulberry32 } from './rng';

/** What the save keeps of offline work and kidnapping (`PlayerProgress.offline`; absent until first used). */
export interface OfflineState {
  /** The job picked for the time away, or null: the one the best trade suits (`defaultJob`). */
  job: OfflineJobId | null;
  /** Paid in goods instead of silver (only jobs with `goods`). */
  goods: boolean;
  /** ชื่อฉาว: sacks sold, less what has faded (`INFAMY.decaySeconds` of play a point). */
  infamy: number;
  /** Seconds of play toward the next point fading. */
  infamyT: number;
  /** Sacks sold, and times this character was kidnapped while away. */
  sold: number;
  taken: number;
  /**
   * Where the character wakes on the next World built (systems/offlineWork.ts reads and clears it): at the
   * job's spot, or beside the buyer it was sold to. Null: where the save left it.
   */
  wake: { at: 'work'; job: OfflineJobId } | { at: 'buyer'; buyer: string } | null;
}

export const newOffline = (): OfflineState => ({ job: null, goods: false, infamy: 0, infamyT: 0, sold: 0, taken: 0, wake: null });

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

/** A saved state repaired against the data (unknown jobs and buyers dropped). */
export function parseOffline(v: unknown): OfflineState {
  const r = (v && typeof v === 'object' ? v : {}) as Partial<OfflineState>;
  const w = r.wake as { at?: unknown; job?: unknown; buyer?: unknown } | null | undefined;
  const wake: OfflineState['wake'] =
    w?.at === 'work' && isOfflineJobId(w.job)
      ? { at: 'work', job: w.job }
      : w?.at === 'buyer' && SACK_BUYERS.some((b) => b.id === w.buyer)
        ? { at: 'buyer', buyer: w.buyer as string }
        : null;
  return {
    job: isOfflineJobId(r.job) ? r.job : null,
    goods: r.goods === true,
    infamy: count(r.infamy),
    infamyT: typeof r.infamyT === 'number' && r.infamyT > 0 ? Math.min(r.infamyT, INFAMY.decaySeconds) : 0,
    sold: count(r.sold),
    taken: count(r.taken),
    wake,
  };
}

/** The progress's offline state, made on first use. */
export function offlineOf(p: PlayerProgress): OfflineState {
  return (p.offline ??= newOffline());
}

/** A trade's level in this progress (1 if never worked). */
const levelIn = (p: PlayerProgress, prof: NonNullable<(typeof OFFLINE_JOBS)[OfflineJobId]['prof']>): number =>
  tradeLevel(p.trades.xp[prof] ?? 0).level;

/** The job a character works without having picked one: the job of their best trade (ties: the list's order), else hauling. */
export function defaultJob(p: PlayerProgress): OfflineJobId {
  let best: OfflineJobId = 'porter';
  let bestXp = 0;
  for (const j of OFFLINE_JOB_LIST) {
    if (!j.prof) continue;
    const xp = p.trades.xp[j.prof] ?? 0;
    if (xp > bestXp) {
      best = j.id;
      bestXp = xp;
    }
  }
  return best;
}

/** The job worked while away: the one picked, else `defaultJob`. */
export const jobOf = (p: PlayerProgress): OfflineJobId => p.offline?.job ?? defaultJob(p);

/** Silver an hour at a job for this character (its trade's level raises it). */
export function jobPay(p: PlayerProgress, job: OfflineJobId): number {
  const j = OFFLINE_JOBS[job];
  const lv = j.prof ? levelIn(p, j.prof) : 1;
  return Math.round(j.silver * (1 + OFFLINE_WORK.perLevel * (lv - 1)));
}

/** Silver earned working `job` for `seconds` (capped as the offline cultivation is). */
export function jobEarnings(p: PlayerProgress, job: OfflineJobId, seconds: number): number {
  const s = Math.min(Math.max(0, seconds), CULTIVATION.offlineCapSeconds);
  return Math.floor((jobPay(p, job) * s) / 3600);
}

/** Goods earned working `job` for `seconds` (empty if the job pays no goods). */
export function jobGoods(p: PlayerProgress, job: OfflineJobId, seconds: number): { id: string; n: number }[] {
  const j = OFFLINE_JOBS[job];
  if (!j.goods) return [];
  const s = Math.min(Math.max(0, seconds), CULTIVATION.offlineCapSeconds);
  const lv = j.prof ? levelIn(p, j.prof) : 1;
  const mult = 1 + OFFLINE_WORK.perLevel * (lv - 1);
  return j.goods.map((g) => ({ id: g.id, n: Math.floor((g.per * mult * s) / 3600) })).filter((g) => g.n > 0);
}

/** Trade xp earned working `job` for `seconds` (capped as the silver is; plain work gives none). */
export function jobXp(job: OfflineJobId, seconds: number): number {
  if (!OFFLINE_JOBS[job].prof) return 0;
  const s = Math.min(Math.max(0, seconds), CULTIVATION.offlineCapSeconds);
  return Math.floor((OFFLINE_WORK.xpPerHour * s) / 3600);
}

/** ชื่อฉาว now. */
export const infamyOf = (p: PlayerProgress): number => p.offline?.infamy ?? 0;
/** The name tag reads red. */
export const isInfamous = (p: PlayerProgress): boolean => infamyOf(p) >= INFAMY.red;
/** What ชื่อฉาว does to the merchant's prices (×1, or ×1.2 once the name reads red). */
export const infamyPriceMult = (p: PlayerProgress): number => (isInfamous(p) ? INFAMY.priceMult : 1);
/** The town guards will not talk. */
export const shunnedByGuards = (p: PlayerProgress): boolean => infamyOf(p) >= INFAMY.guards;

/** How exposed a job's spot is in a zone (0–1); the default where the zone has no spot for it. */
export const jobRisk = (zone: string, job: OfflineJobId): number =>
  spotsFor(zone, job)[0]?.risk ?? OFFLINE_WORK.defaultRisk;

/** The chance of being kidnapped while away for `seconds` at a spot of `risk`. */
export function kidnapChance(seconds: number, risk: number): number {
  const h = Math.min(Math.max(0, seconds), CULTIVATION.offlineCapSeconds) / 3600;
  return 1 - Math.exp(-h * OFFLINE_WORK.kidnapRate * Math.max(0, Math.min(1, risk)));
}

/** What the time away came to (the offline report). */
export interface AwayReport {
  /** Seconds away. */
  seconds: number;
  /** Seconds worked: capped, and cut short by a kidnapping. */
  worked: number;
  job: OfflineJobId;
  /** Silver earned (already in the progress). */
  silver: number;
  /** Goods earned instead of silver (already in the progress); empty on the coin path. */
  goods: { id: string; n: number }[];
  /** Trade xp for the time worked (trade jobs only; already in the progress). */
  xp: number;
  /** แต้มฝึกปรือ for the time worked (`offlineGain(worked)`; the caller credits it). */
  cultivation: number;
  /** Long enough away for the report to show and the character to be set down at work. */
  shown: boolean;
  /** Kidnapped while away: after how many seconds of work, and to whom the sack was sold. */
  kidnap: { after: number; buyer: SackBuyer; lost: number } | null;
}

/** A small stable hash of a string (FNV-1a), for the seed. */
function fnv(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Settles the time away: pays the silver into the progress, counts a kidnapping and leaves `offline.wake`
 * for the World. `zone` is where the character will wake (the buyers and work spots are looked up there;
 * a zone without buyers takes the first zone that has some). The draw is seeded by the save's `savedAt` and
 * the name, so the same save and time away always give the same report. Cultivation is not touched: the
 * caller credits `report.cultivation`.
 */
export function settleAway(p: PlayerProgress, seconds: number, zone: string = p.location): AwayReport {
  const away = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const job = jobOf(p);
  const shown = away >= OFFLINE_WORK.reportMin;
  let kidnap: AwayReport['kidnap'] = null;
  if (shown && rankFor(p.cultivationSpent).index >= KIDNAP.rankFloor) {
    const rng = mulberry32((Math.floor(p.savedAt / 1000) ^ fnv(p.name)) | 0);
    const capped = Math.min(away, CULTIVATION.offlineCapSeconds);
    if (rng() < kidnapChance(capped, jobRisk(zone, job))) {
      const buyers = buyersIn(zone).length ? buyersIn(zone) : buyersIn(SACK_BUYERS[0]?.zone ?? '');
      const buyer = buyers[Math.floor(rng() * buyers.length)];
      if (buyer) kidnap = { after: Math.floor(rng() * capped), buyer, lost: 0 };
    }
  }
  const worked = kidnap ? kidnap.after : away;
  const inGoods = !!OFFLINE_JOBS[job].goods && p.offline?.goods === true;
  let silver = inGoods ? 0 : jobEarnings(p, job, worked);
  const goods = inGoods ? jobGoods(p, job, worked) : [];
  for (const g of goods) p.trades.goods[g.id] = (p.trades.goods[g.id] ?? 0) + g.n;
  if (kidnap && !inGoods) {
    // The sale is paid half out of this character's away earnings (price at distance 0: the carry is not simulated).
    kidnap.lost = sackVictimLoss(sackPrice(rankFor(p.cultivationSpent).index, 0), silver);
    silver -= kidnap.lost;
  }
  p.gold += silver;
  const xp = jobXp(job, worked);
  const prof = OFFLINE_JOBS[job].prof;
  if (prof && xp > 0) p.trades.xp[prof] = (p.trades.xp[prof] ?? 0) + xp;
  if (shown) {
    const st = offlineOf(p);
    if (kidnap) st.taken++;
    st.wake = kidnap ? { at: 'buyer', buyer: kidnap.buyer.id } : { at: 'work', job };
    // The body stood outside at work (or was carried off): not still indoors.
    p.indoors = null;
  }
  return { seconds: away, worked, job, silver, goods, xp, cultivation: offlineGain(worked), shown, kidnap };
}
